import { useControllerConnectionStatus } from '../Hooks/useControllerConnectionStatus';
import React, { useState, useEffect } from 'react';
import Typography from '@material-ui/core/Typography';
import FormHelperText from '@material-ui/core/FormHelperText';
import Desc from '../FormWidgets/Description';
import { Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Button from '@material-ui/core/Button';
import TabContext from '@material-ui/lab/TabContext';
import TabList from '@material-ui/lab/TabList';
import TabPanel from '@material-ui/lab/TabPanel';
import Box from '@material-ui/core/Box';
import { AppBar, Tab, Grid, makeStyles } from '@material-ui/core';
import { isMobile } from 'react-device-detect';

import { ScheduledSetting as ShowSetting, ScheduleMarker, ScheduleDraftStatus, useScheduledSettings } from './Scheduling/ScheduledSettings';
import { SettingGroupGrid, ValueBox, useExpansionState } from '../FormWidgets/SettingGroup';
import { LoadSettingsDialog } from '../Utils/load_settings';

import { useTabSettings } from './settingFunctions';
import { SettingActions } from '../components';
import { useTranslation } from 'react-i18next';
import { FlexSelect } from '../FormWidgets/Select';
import { nutritionPumpTypeMapping, nutritionPumpUnitMapping } from '../Constants/constants';

import { ExpandMore } from '../Utils/Expand';
import { EditableLabel } from '../FormWidgets/EditableLabel';
import _ from 'lodash';
import { useParams } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import getStateManager from '../StateManager';
import {
  hasTankSettings, supportsNutritionTanks, isInvalidNutrientPumpUnit,
  hasInvalidNutrientPumpUnit, hasInvalidNutrientSettingsUnit
} from '../Utils/nutrition_utils';
import { getControllerApiVersion } from '../Utils/utils';
import { useNutritionSettings } from '../Hooks/useNutritionSettings';
import NutritionTanks from './NutritionTanks';
import { isZoned } from '../Utils/utils';

export {
  isInvalidNutrientPumpUnit, hasInvalidNutrientPumpUnit, hasInvalidNutrientSettingsUnit
} from '../Utils/nutrition_utils';

const useStyles = makeStyles((theme) => ({
  icon: {
    color: theme.palette.text.primary
  },
  pumpActions: {
    display: 'flex',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      alignItems: 'stretch',
      flexDirection: 'column',
      marginLeft: 'auto',
      marginRight: 'auto',
      maxWidth: 280,
      width: '100%'
    }
  },
  pumpActionButton: {
    [theme.breakpoints.down('sm')]: {
      '&&': {
        margin: '6px 0',
        width: '100%'
      }
    }
  }
}));

export function PumpActions({ addDisabled, deleteDisabled, onAdd, onDelete }) {
  const { t } = useTranslation();
  const classes = useStyles();

  return (
    <div className={classes.pumpActions}>
      <Button
        className={classes.pumpActionButton}
        color="primary"
        onClick={onAdd}
        disabled={addDisabled}
      >
        {t('add_pump')}
      </Button>
      <Button
        className={classes.pumpActionButton}
        color="primary"
        onClick={onDelete}
        disabled={deleteDisabled}
      >
        {t('del_last_pump')}
      </Button>
    </div>
  );
}

export default function MultipleNutrientSettings() {
  const params = useParams();
  const { controller, parent } = getStateManager().getControllerForTab(params);
  const settings = useSnapshot(parent.settings);
  return hasTankSettings(settings.nutrition) ? (
    <TankNutrientSettings
      key={`${controller.id}:${parent.zone_id}`}
      controller={controller}
      nutrition={settings.nutrition}
    />
  ) : (
    <LegacyMultipleNutrientSettings />
  );
}

function TankNutrientSettings({ controller, nutrition }) {
  const { t } = useTranslation();
  const controllerSnapshot = useSnapshot(controller);
  if (supportsNutritionTanks(controllerSnapshot)) return <SupportedTankNutrientSettings />;
  // The latest server normalizes older controllers' four recipes to schema 2.
  // Empty tank/catalog arrays do not establish controller support for tanks.
  if (
    getControllerApiVersion(controllerSnapshot) &&
    Array.isArray(nutrition.nutrient_settings) &&
    nutrition.nutrient_settings.length === 4 &&
    nutrition.tanks.length === 0 &&
    nutrition.routes?.length === 0
  )
    return <LegacyMultipleNutrientSettings />;
  return (
    <Box display="flex" justifyContent="center" mt={6}>
      {t('update_controller_settings')}
    </Box>
  );
}

function SupportedTankNutrientSettings() {
  const tab = useNutritionSettings();
  return <MultipleNutritionForm {...tab} />;
}

const legacyNutritionSettings = (values) => _.pick(values, 'nutrient_settings');

function LegacyMultipleNutrientSettings() {
  const tab = useTabSettings('nutrition', [], legacyNutritionSettings);
  return <MultipleNutritionForm {...tab} />;
}

function MultipleNutritionForm({
  t,
  controller,
  parent,
  controllerSnapshot,
  parentSnapshot,
  values: ordinaryValues,
  controls: ordinaryControls,
  loadSettingsControls = ordinaryControls,
  openLoadSettingsDialog,
  closeLoadSettingsDialog,
  tankSettings = false,
  saveDisabled = false,
  invalid
}) {
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);

  const [mobile] = useState(isMobile);

  const schedule = useScheduledSettings(controller, parent, 'nutrition', ordinaryValues, ordinaryControls);
  const { values, controls } = schedule;
  const write_access = controllerSnapshot.access_rights.nutrition.write;
  const hasInvalidPumpUnit = hasInvalidNutrientSettingsUnit(values.nutrient_settings);
  const saveSettings = () => {
    if (!hasInvalidPumpUnit) controls.saveSettings();
  };

  const addPump = (id) => {
    if (values.nutrient_settings[id].pump_cnt_used < 8) {
      let pumps = [...values.nutrient_settings[id].pumps];
      pumps.push({ type: 0, unit: 0, value: 0, name: '' });

      controls.setValue(
        [`nutrient_settings[${id}].pump_cnt_used`, `nutrient_settings[${id}].pumps`],
        [values.nutrient_settings[id].pump_cnt_used + 1, pumps]
      );
    }
  };
  const delLastPump = (id) => {
    if (values.nutrient_settings[id].pump_cnt_used > 1) {
      let pumps = [...values.nutrient_settings[id].pumps];
      pumps.pop();
      controls.setValue(
        [`nutrient_settings[${id}].pump_cnt_used`, `nutrient_settings[${id}].pumps`],
        [values.nutrient_settings[id].pump_cnt_used - 1, pumps]
      );
    }
  };

  // Tab handling for nutrient settings
  const nutrientIds = Object.keys(values.nutrient_settings ?? {});
  const [section, setSection] = useState(nutrientIds.length ? 'recipes' : 'tanks');
  const visibleSection = nutrientIds.length ? section : 'tanks';
  const [activeNutrient, setActiveNutrient] = useState(nutrientIds[0] || '');
  useEffect(() => {
    if (!nutrientIds.includes(activeNutrient) && nutrientIds.length) {
      setActiveNutrient(nutrientIds[0]);
    }
  }, [nutrientIds, activeNutrient]);

  return (
    <React.Fragment>
      <ScheduleDraftStatus schedule={schedule} />
      <LoadSettingsDialog
        open={openLoadSettingsDialog}
        handleClose={closeLoadSettingsDialog}
        controls={loadSettingsControls}
      />

      {tankSettings && (
        <>
          <TabContext value={visibleSection}>
            <AppBar position="static" color="default">
              <TabList
                centered
                indicatorColor="primary"
                textColor="primary"
                onChange={(_, value) => setSection(value)}
                aria-label={t('nutrition')}
              >
                {(nutrientIds.length ? ['recipes', 'tanks'] : ['tanks']).map((id) => (
                  <Tab key={id} value={id} label={t(`nutrition_${id}`)} />
                ))}
              </TabList>
            </AppBar>
          </TabContext>
          {invalid && (
            <Box mt={2} role="status">
              <Typography color="error">{t('nutrition_invalid_settings')}</Typography>
            </Box>
          )}
        </>
      )}
      {schedule.canSave && tankSettings && visibleSection === 'tanks' && (
        <SettingActions
          title={connectionDisabledReason}
          onReset={controls.resetSettings}
          onSave={saveSettings}
          on_default={schedule?.saving || connectionStatus !== 'good' || hasInvalidPumpUnit || saveDisabled}
        />
      )}

      {(!tankSettings || visibleSection === 'recipes') && nutrientIds.length > 0 && (
        <TabContext value={activeNutrient}>
          <AppBar
            position="static"
            color="default"
            style={
              mobile
                ? {
                    boxSizing: 'border-box',
                    margin: tankSettings ? '16px 0' : '0 0 16px',
                    padding: '0 8px',
                    width: '100%'
                  }
                : tankSettings
                  ? { margin: '24px 0' }
                  : { margin: '-24px -224px 24px', width: 'calc(100% + 448px)' }
            }
          >
            <Box display="flex" justifyContent="center" width="100%">
              <TabList
                variant="scrollable"
                scrollButtons="auto"
                onChange={(_, v) => setActiveNutrient(v)}
              >
                {nutrientIds.map((nid, idx) => (
                  <Tab
                    key={nid}
                    value={nid}
                    label={`${t(isZoned(controller) || nutrientIds.length === 4 ? 'sub_zone' : 'nutrition')} ${isZoned(controller) || nutrientIds.length === 4 ? Number(nid) + 1 : idx + 1}`}
                    id={`nutrient-tab-${idx}`}
                    aria-controls={`nutrient-tabpanel-${idx}`}
                  />
                ))}
              </TabList>
            </Box>
          </AppBar>

          {schedule.canSave && (
            <SettingActions
              title={connectionDisabledReason}
              onReset={controls.resetSettings}
              onSave={saveSettings}
              on_default={schedule?.saving || connectionStatus !== 'good' || hasInvalidPumpUnit || saveDisabled}
            />
          )}

          {nutrientIds.map((id, idx) => (
            <TabPanel key={id} value={id} id={`nutrient-tabpanel-${idx}`} style={{ padding: 0 }}>
              <SettingGroupGrid controller={controllerSnapshot} id={'nutrient_solution'}>
                <ShowSetting
                  schedule={schedule}
                  id={`nutrient_settings[${id}].ec_setpoint`}
                  show={'ec_setpoint'}
                  controller={controllerSnapshot}
                  cur_value={values.nutrient_settings[id].ec_setpoint}
                  controller_value={parentSnapshot.settings.nutrition.nutrient_settings[id]?.ec_setpoint}
                  controls={controls}
                  disabled={schedule.disabled(`nutrient_settings[${id}].ec_setpoint`, undefined, !write_access)}
                  type="float"
                  num_digits={2}
                />
                <ShowSetting
                  schedule={schedule}
                  id={`nutrient_settings[${id}].ph_setpoint`}
                  show={'ph_setpoint'}
                  controller={controllerSnapshot}
                  cur_value={values.nutrient_settings[id].ph_setpoint}
                  controller_value={parentSnapshot.settings.nutrition.nutrient_settings[id]?.ph_setpoint}
                  controls={controls}
                  disabled={schedule.disabled(`nutrient_settings[${id}].ph_setpoint`, undefined, !write_access)}
                  type="float"
                  num_digits={2}
                />
                <ShowSetting
                  schedule={schedule}
                  id={`nutrient_settings[${id}].temp_setpoint`}
                  show={'temp_setpoint'}
                  controller={controllerSnapshot}
                  cur_value={values.nutrient_settings[id].temp_setpoint}
                  controller_value={parentSnapshot.settings.nutrition.nutrient_settings[id]?.temp_setpoint}
                  controls={controls}
                  disabled={schedule.disabled(`nutrient_settings[${id}].temp_setpoint`, undefined, !write_access)}
                  type="float"
                />
              </SettingGroupGrid>

              {values.nutrient_settings[id].pumps.map((pump, i) => (
                <Pump
                  schedule={schedule}
                  key={i}
                  expand_id={`nutrition_pump_${i}`}
                  id={id}
                  index={i}
                  name={pump.name}
                  values={values}
                  controller={controllerSnapshot}
                  parent={parentSnapshot}
                  value_path_prefix={`nutrient_settings[${id}].`}
                  controls={controls}
                  write_access={write_access}
                />
              ))}
              <PumpActions
                onAdd={() => addPump(id)}
                onDelete={() => delLastPump(id)}
                addDisabled={values.nutrient_settings[id].pump_cnt_used >= 8 || schedule.disabled('pump_cnt_used', id, !write_access)}
                deleteDisabled={values.nutrient_settings[id].pump_cnt_used <= 1 || schedule.disabled('pump_cnt_used', id, !write_access)}
              />
            </TabPanel>
          ))}
        </TabContext>
      )}

      {tankSettings && visibleSection === 'tanks' && (
        <NutritionTanks
          schedule={schedule}
          controller={controller}
          parent={parent}
          values={values}
          controls={controls}
        />
      )}

      {schedule.canSave && !tankSettings && (
        <SettingActions
          title={connectionDisabledReason}
          onReset={controls.resetSettings}
          onSave={saveSettings}
          on_default={schedule?.saving || connectionStatus !== 'good' || hasInvalidPumpUnit}
        />
      )}
    </React.Fragment>
  );
}

export function NutrientSettings() {
  const params = useParams();
  const { controller, parent } = getStateManager().getControllerForTab(params);
  const settings = useSnapshot(parent.settings);
  return hasTankSettings(settings.nutrition) ? (
    <TankNutrientSettings
      key={`${controller.id}:${parent.zone_id}`}
      controller={controller}
      nutrition={settings.nutrition}
    />
  ) : Array.isArray(settings.nutrition?.nutrient_settings) ? (
    <LegacyMultipleNutrientSettings />
  ) : (
    <LegacyNutrientSettings />
  );
}

function LegacyNutrientSettings() {
  const {
    controller,
    parent,
    controllerSnapshot,
    parentSnapshot,
    values: ordinaryValues,
    controls: ordinaryControls,
    openLoadSettingsDialog,
    closeLoadSettingsDialog
  } = useTabSettings();
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);

  const schedule = useScheduledSettings(controller, parent, 'nutrition', ordinaryValues, ordinaryControls);
  const { values, controls } = schedule;
  const write_access = controllerSnapshot.access_rights.nutrition.write;
  const hasInvalidPumpUnit = hasInvalidNutrientPumpUnit(values.pumps);
  const saveSettings = () => {
    if (!hasInvalidPumpUnit) controls.saveSettings();
  };

  const addPump = () => {
    if (values.pump_cnt_used < 8) {
      let pumps = [...values.pumps];
      pumps.push({ type: 0, unit: 0, value: 0, name: '' });

      controls.setValue(['pump_cnt_used', 'pumps'], [values.pump_cnt_used + 1, pumps]);
    }
  };
  const delLastPump = () => {
    if (values.pump_cnt_used > 1) {
      let pumps = [...values.pumps];
      pumps.pop();
      controls.setValue(['pump_cnt_used', 'pumps'], [values.pump_cnt_used - 1, pumps]);
    }
  };

  return (
    <React.Fragment>
      <ScheduleDraftStatus schedule={schedule} />
      <LoadSettingsDialog
        open={openLoadSettingsDialog}
        handleClose={closeLoadSettingsDialog}
        controls={ordinaryControls}
      />

      {schedule.canSave && (
        <SettingActions
          title={connectionDisabledReason}
          onReset={controls.resetSettings}
          onSave={saveSettings}
          on_default={schedule?.saving || connectionStatus !== 'good' || hasInvalidPumpUnit}
        />
      )}

      <SettingGroupGrid controller={controllerSnapshot} id={'nutrient_solution'}>
        <ShowSetting
          schedule={schedule}
          id="ec_setpoint"
          controller={controllerSnapshot}
          cur_value={values.ec_setpoint}
          controller_value={parentSnapshot.settings.nutrition.ec_setpoint}
          controls={controls}
          disabled={schedule.disabled('ec_setpoint', undefined, !write_access)}
          type="float"
          num_digits={2}
        />
        <ShowSetting
          schedule={schedule}
          id="ph_setpoint"
          controller={controllerSnapshot}
          cur_value={values.ph_setpoint}
          controller_value={parentSnapshot.settings.nutrition.ph_setpoint}
          controls={controls}
          disabled={schedule.disabled('ph_setpoint', undefined, !write_access)}
          type="float"
          num_digits={2}
        />
        <ShowSetting
          schedule={schedule}
          id="temp_setpoint"
          controller={controllerSnapshot}
          cur_value={values.temp_setpoint}
          controller_value={parentSnapshot.settings.nutrition.temp_setpoint}
          controls={controls}
          disabled={schedule.disabled('temp_setpoint', undefined, !write_access)}
          type="float"
        />
      </SettingGroupGrid>

      {values.pumps.map((pump, i) => (
        <Pump
          schedule={schedule}
          expand_id={`nutrition_pump_${i}`}
          index={i}
          controller={controllerSnapshot}
          parent={parentSnapshot}
          values={values}
          controls={controls}
          write_access={write_access}
        />
      ))}
      <PumpActions
        onAdd={addPump}
        onDelete={delLastPump}
        addDisabled={values.pump_cnt_used >= 8 || schedule.disabled('pump_cnt_used', undefined, !write_access)}
        deleteDisabled={values.pump_cnt_used <= 1 || schedule.disabled('pump_cnt_used', undefined, !write_access)}
      />

      {schedule.canSave && (
        <SettingActions
          title={connectionDisabledReason}
          onReset={controls.resetSettings}
          onSave={saveSettings}
          on_default={schedule?.saving || connectionStatus !== 'good' || hasInvalidPumpUnit}
        />
      )}
    </React.Fragment>
  );
}

const default_pump = { name: '', type: 0, value: 0, unit: 0 };

export function Pump({
  expand_id,
  id,
  index,
  name = '',
  values,
  controls,
  controller,
  parent,
  write_access,
  schedule,
  value_path_prefix = '',
  edit_name = true
}) {
  let { t } = useTranslation();
  const classes = useStyles();

  let p_path = `${value_path_prefix}pumps[${index}]`;
  ['name', 'type', 'unit', 'value'].forEach((field) => controls.retainSetting?.(`${p_path}.${field}`));
  const controllerPump = _.get(parent.settings.nutrition, p_path);
  let pump = _.get(values, p_path, {});
  pump = { ...default_pump, ...pump };
  const invalidUnit = isInvalidNutrientPumpUnit(pump);
  const [expand, setExpand] = useExpansionState(expand_id);

  return (
    <SettingGroupGrid label={t('pump') + ' ' + (index + 1)} fullWidth={true}>
      <Grid container>
        <Grid item xs={4}>
          {edit_name && write_access && (controllerPump || !schedule?.(`${p_path}.type`)) ? (
            <EditableLabel
              label={'my_memo'}
              id={`${p_path}.name`}
              value={pump.name ?? -1}
              controller_value={controllerPump?.name ?? -1}
              controls={controls}
              handleInput={(_, event, value) => controls.setValue(`${p_path}.name`, value)}
              no_label={true}
            />
          ) : (
            <>{pump.name || name}</>
          )}
        </Grid>
        <Grid item xs={4}>
          {t(nutritionPumpTypeMapping[pump.type] ?? nutritionPumpTypeMapping['default'])}
        </Grid>
      </Grid>

      <div style={{ textAlign: 'center', marginTop: '-5em', marginLeft: '45%', width: '10%' }}>
        <ExpandMore
          expand={expand}
          onClick={() => setExpand(!expand)}
          aria-expanded={expand}
          aria-label="show more"
        >
          <ExpandMoreIcon fontSize="large" className={classes.icon} />
        </ExpandMore>
      </div>

      <Collapse in={expand} timeout="auto" unmountOnExit>
        <Grid container spacing={1} columns={{ xs: 6, md: 12 }}>
          <Grid item xs={12} md={6}>
            <ShowSetting
              schedule={schedule}
              id={`${p_path}.value` /* used as key for controls.setValue() */}
              show="pump_dosage"
              unit_key={value_path_prefix === '' ? 'pumps.value' : undefined}
              controller={controller}
              cur_value={pump.value}
              controller_value={controllerPump?.value ?? -1}
              controls={controls}
              unit={t(nutritionPumpUnitMapping[pump.unit] ?? nutritionPumpUnitMapping['default'])}
              disabled={pump.type === 0 || pump.type >= 2 || (schedule?.disabled(`${p_path}.value`, undefined, !write_access) ?? !write_access)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ValueBox controller={controller} id={'type'}>
              <Typography gutterBottom>
                <ScheduleMarker target={schedule?.(`${p_path}.type`)} />
                <Desc id="pump_type" />
              </Typography>
              <FlexSelect
                id={`${p_path}.type`}
                controller={controller}
                value={pump.type}
                controller_value={controllerPump?.type ?? -1}
                controls={controls}
                disabled={schedule?.disabled(`${p_path}.type`, undefined, !write_access) ?? !write_access}
                mapping={nutritionPumpTypeMapping}
                onChange={(event) => {
                  const type = Number(event.target.value);
                  const paths = [`${p_path}.type`];
                  const nextValues = [type];

                  if (type === 1 && Number(pump.unit ?? 0) === 0) {
                    paths.push(`${p_path}.unit`);
                    nextValues.push(1);
                  }

                  controls.setValue(paths, nextValues);
                }}
              />
            </ValueBox>
            <ValueBox controller={controller} id={'pump_unit'}>
              <Typography gutterBottom>
                <ScheduleMarker target={schedule?.(`${p_path}.unit`)} />
                <Desc id="pump_unit" />
              </Typography>
              <FlexSelect
                id={`${p_path}.unit`}
                controller={controller}
                value={pump.unit}
                controller_value={controllerPump?.unit ?? -1}
                controls={controls}
                mapping={nutritionPumpUnitMapping}
                disabledValues={Number(pump.type) === 1 ? [0] : []}
                onChange={(event) => controls.setValue(`${p_path}.unit`, event.target.value)}
                disabled={Number(pump.type) !== 1 || (schedule?.disabled(`${p_path}.unit`, undefined, !write_access) ?? !write_access)}
                error={invalidUnit}
              />
              {invalidUnit && (
                <FormHelperText error>{t('nutrient_pump_unit_required')}</FormHelperText>
              )}
            </ValueBox>
          </Grid>
        </Grid>
      </Collapse>
    </SettingGroupGrid>
  );
}
